import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
// TABLE-PASTE-FIX: confirmed live — pasting a table (from Word/Excel/Google
// Docs) silently lost all row/column structure and came out as plain
// paragraphs. Root cause: StarterKit's default schema has no table node at
// all, so ProseMirror's paste parser has nowhere to put <table>/<tr>/<td>
// content and unwraps it to plain text instead of rejecting the paste.
// These four extensions register the table node types the schema was
// missing — paste (and the toolbar button below) now round-trip real
// <table> markup instead of flattening it.
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import { authFetch } from '../services/api';
import { useToast } from '../hooks/useToast';

// SMART-PASTE-HEADING-FIX: writers drafting SEO blog content very commonly
// mark heading levels inline as plain text ("How It Works - h2",
// "Training data - h3", "H3- Code completion:") rather than in real
// markup — confirmed live twice with the same user, who pasted content in
// exactly this shape expecting it to become real <h2>/<h3> tags, and it
// silently stayed plain paragraph text instead (the editor's toolbar has
// no way to auto-detect this). Detect these markers on paste and convert
// them to real heading tags automatically, instead of requiring every
// heading line to be manually reselected and reformatted afterward.
const HEADING_TRAILING_RE = /\s*[-–—]\s*h([123])\s*(tag)?\s*$/i;
const HEADING_LEADING_RE = /^h([123])\s*[-–—]\s*/i;
// PASTE-GATE-MULTILINE-FIX: handlePaste below runs these same two regexes
// against the ENTIRE pasted blob first, to decide whether to intercept the
// paste at all — without the `m` flag, `^`/`$` anchor to the start/end of
// the whole string, not each line. Confirmed live: a realistic paste (an
// intro paragraph, then "Overview - h2", more body text, then "Details -
// h3", then a closing paragraph) matches neither regex at all, because the
// heading markers sit in the MIDDLE of the pasted block, not on its very
// first or very last line — the gate silently rejects the whole paste and
// NOTHING converts, not even the headings that formatPastedTextAsHtml's
// per-line loop would have handled correctly. Separate multiline-flagged
// copies, used ONLY for this gate check — the per-line loop in
// formatPastedTextAsHtml still uses the original (correct, line-scoped)
// regexes above via String.match on each already-split line.
const HEADING_TRAILING_RE_GATE = new RegExp(HEADING_TRAILING_RE.source, 'im');
const HEADING_LEADING_RE_GATE = new RegExp(HEADING_LEADING_RE.source, 'im');

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function formatPastedTextAsHtml(text) {
  const lines = text.split(/\r?\n/);
  const htmlParts = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const trailingMatch = line.match(HEADING_TRAILING_RE);
    const leadingMatch = !trailingMatch ? line.match(HEADING_LEADING_RE) : null;

    let level = null;
    let text_ = line;
    if (trailingMatch) {
      level = trailingMatch[1];
      text_ = line.slice(0, trailingMatch.index).trim();
    } else if (leadingMatch) {
      level = leadingMatch[1];
      text_ = line.slice(leadingMatch[0].length).trim();
    }

    if (!text_) continue;

    if (level === '2' || level === '3') {
      htmlParts.push(`<h${level}>${escapeHtml(text_)}</h${level}>`);
    } else {
      // level === '1' (the page's H1 is the separate Title field — strip
      // the marker instead of emitting a second H1 into the body) or no
      // marker at all — either way, just a normal paragraph.
      htmlParts.push(`<p>${escapeHtml(text_)}</p>`);
    }
  }
  return htmlParts.join('');
}

// TABLE-DROPPED-ON-HEADING-PASTE-FIX: confirmed live — a paste containing
// BOTH heading markers ("H2 - ...") AND a real table (copied from Word/
// Excel/Google Docs/a webpage) lost the table entirely. Cause: the gate
// above only ever looks at clipboard `text/plain` — once it matches (the
// heading markers are present ANYWHERE in the paste), the handler discards
// the clipboard's real `text/html` altogether and rebuilds the WHOLE paste
// from plain text via formatPastedTextAsHtml, which only knows about
// lines/paragraphs and has no concept of a table. The table's row/column
// structure was never actually broken by the Table extension (see the
// TABLE-PASTE-FIX above it) — it was being thrown away before the editor
// ever saw it, by this OTHER paste path taking priority.
// Fix: when the clipboard's `text/html` contains a real <table>, don't
// drop down to the plain-text rebuild at all. Operate on the real HTML DOM
// instead — convert only the heading-marker paragraphs to real <h2>/<h3>
// tags in place, and leave every other node (including the whole table)
// exactly as the browser pasted it.
function convertHeadingMarkersInHtml(root) {
  const blocks = root.querySelectorAll('p, div, li');
  blocks.forEach((el) => {
    if (el.closest('table')) return; // never touch text inside a table cell
    if (el.querySelector('table, p, div, li')) return; // only leaf-ish blocks
    const line = (el.textContent || '').trim();
    if (!line) return;

    const trailingMatch = line.match(HEADING_TRAILING_RE);
    const leadingMatch = !trailingMatch ? line.match(HEADING_LEADING_RE) : null;
    if (!trailingMatch && !leadingMatch) return;

    const level = (trailingMatch || leadingMatch)[1];
    const cleanText = trailingMatch
      ? line.slice(0, trailingMatch.index).trim()
      : line.slice(leadingMatch[0].length).trim();
    if (!cleanText) return;

    if (level === '2' || level === '3') {
      const heading = document.createElement(`h${level}`);
      heading.textContent = cleanText;
      el.replaceWith(heading);
    } else {
      el.textContent = cleanText;
    }
  });
}

const EMPTY_FORM = {
  id: null, title: '', slug: '', excerpt: '', content: '',
  cover_image_url: '', cover_image_alt: '',
  focus_keyword: '', tags: '', seo_title: '', meta_description: '',
  status: 'draft',
};

// Must match backend's MAX_IMAGE_SIZE (backend/api/uploads.py)
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

// Cosmetic-only preview slug for NEW posts (no real slug assigned yet) —
// the backend is the source of truth for the actual unique slug.
function previewSlugify(text) {
  return (text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'post';
}

function RichTextToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, label, title) => (
    <button
      type="button"
      className={`abm-toolbtn${active ? ' abm-toolbtn--active' : ''}`}
      onClick={onClick}
      title={title}
    >
      {label}
    </button>
  );
  return (
    <div className="abm-toolbar">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'B', 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'I', 'Italic')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2', 'Heading 2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'H3', 'Heading 3')}
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), '• List', 'Bullet list')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), '1. List', 'Numbered list')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), '"', 'Quote')}
      {btn(false, () => {
        const url = window.prompt('Link URL');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }, '🔗', 'Add link')}
      {btn(editor.isActive('table'), () => {
        if (editor.isActive('table')) {
          editor.chain().focus().deleteTable().run();
        } else {
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
        }
      }, '▦ Table', editor.isActive('table') ? 'Remove table' : 'Insert table')}
      {editor.isActive('table') && (
        <>
          {btn(false, () => editor.chain().focus().addColumnAfter().run(), '+Col', 'Add column')}
          {btn(false, () => editor.chain().focus().addRowAfter().run(), '+Row', 'Add row')}
          {btn(false, () => editor.chain().focus().deleteColumn().run(), '-Col', 'Delete column')}
          {btn(false, () => editor.chain().focus().deleteRow().run(), '-Row', 'Delete row')}
        </>
      )}
    </div>
  );
}

const SEO_TITLE_MAX = 70;
const META_DESC_MAX = 300;

function SeoSection({ form, setForm }) {
  const previewSlug = form.slug || previewSlugify(form.title);
  const previewTitle = (form.seo_title || form.title || 'Your post title').trim();
  const previewDesc = (form.meta_description || form.excerpt || 'Add a meta description so this preview shows real copy instead of a blank snippet.').trim();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <div className="abm-seo-section">
      <h3 className="abm-seo-heading">SEO</h3>

      <div className="abm-field">
        <label className="abm-label">FOCUS KEYWORD (optional — used to tune AI hints, density, and the SERP preview)</label>
        <input
          className="abm-input"
          value={form.focus_keyword || ''}
          onChange={(e) => setForm((f) => ({ ...f, focus_keyword: e.target.value }))}
          placeholder="e.g. AI website builder"
        />
      </div>

      <div className="abm-field">
        <label className="abm-label">TAGS (optional — comma-separated, added to the page's meta keywords)</label>
        <input
          className="abm-input"
          value={form.tags || ''}
          onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          placeholder="e.g. AI Code Generation, AI in Programming, AI Coding Tools"
        />
      </div>

      <div className="abm-field">
        <label className="abm-label">SEO TITLE (optional — overrides tab title in search results)</label>
        <input
          className="abm-input"
          value={form.seo_title || ''}
          onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value.slice(0, SEO_TITLE_MAX) }))}
          placeholder={form.title || 'e.g. How I hacked my productivity · DS CodeXAI'}
        />
        <p className="abm-charcount">{(form.seo_title || '').length}/{SEO_TITLE_MAX}</p>
      </div>

      <div className="abm-field">
        <label className="abm-label">META DESCRIPTION (optional)</label>
        <textarea
          className="abm-textarea"
          rows={3}
          value={form.meta_description || ''}
          onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value.slice(0, META_DESC_MAX) }))}
          placeholder="Optional search-engine description…"
        />
        <p className="abm-charcount">{(form.meta_description || '').length}/{META_DESC_MAX}</p>
      </div>

      <div className="abm-serp-preview">
        <p className="abm-label">SEARCH RESULT PREVIEW</p>
        <div className="abm-serp-card">
          <div className="abm-serp-url">{origin.replace(/^https?:\/\//, '')} › blog › {previewSlug}</div>
          <div className="abm-serp-title">{previewTitle}</div>
          <div className="abm-serp-desc">{previewDesc}</div>
        </div>
      </div>
    </div>
  );
}

function PostEditor({ form, setForm, onSave, onCancel, saving }) {
  const [uploadingCover, setUploadingCover] = useState(false);
  // HTML-SOURCE-MODE-FEATURE: the rich-text editor is great for writing by
  // hand, but content authored/formatted elsewhere as real HTML (or
  // generated by another tool) needs a way in as-is — typing/pasting
  // `<table>`/`<h2>`/etc. into the WYSIWYG view just shows the literal
  // angle-bracket text instead of being interpreted as markup, since a
  // contentEditable surface treats typed text as text, not source. This
  // toggle swaps the CONTENT field to a plain textarea bound directly to
  // the raw HTML string — no parsing/escaping in the way — and re-syncs
  // the rich-text editor's view from it when switching back.
  const [htmlSourceMode, setHtmlSourceMode] = useState(false);
  const toast = useToast();

  const editor = useEditor({
    extensions: [
      StarterKit,
      ImageExtension,
      LinkExtension,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: form.content || '',
    immediatelyRender: false,
    onUpdate: ({ editor }) => setForm((f) => ({ ...f, content: editor.getHTML() })),
    editorProps: {
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain');
        if (!text) return false;
        // Only take over when heading markers are actually present —
        // otherwise fall through to Tiptap's normal paste handling, which
        // preserves real rich-text formatting (bold, links, etc.) as-is.
        if (!HEADING_TRAILING_RE_GATE.test(text) && !HEADING_LEADING_RE_GATE.test(text)) return false;

        event.preventDefault();
        // TABLE-DROPPED-ON-HEADING-PASTE-FIX: a real <table> in the
        // clipboard's rich HTML must never be flattened through the
        // plain-text rebuild below — operate on the actual HTML instead
        // whenever it's there, and only fall back to text-only
        // reconstruction when the clipboard truly has no HTML to work with.
        const richHtml = event.clipboardData?.getData('text/html');
        if (richHtml && /<table[\s>]/i.test(richHtml)) {
          const dom = document.createElement('div');
          dom.innerHTML = richHtml;
          convertHeadingMarkersInHtml(dom);
          const slice = ProseMirrorDOMParser.fromSchema(view.state.schema).parseSlice(dom);
          view.dispatch(view.state.tr.replaceSelection(slice));
          return true;
        }

        const html = formatPastedTextAsHtml(text);
        const dom = document.createElement('div');
        dom.innerHTML = html;
        const slice = ProseMirrorDOMParser.fromSchema(view.state.schema).parseSlice(dom);
        view.dispatch(view.state.tr.replaceSelection(slice));
        return true;
      },
    },
  });

  useEffect(() => {
    // Sync editor content when switching between create/edit without remounting.
    if (editor && form.id !== editor._loadedForId) {
      editor.commands.setContent(form.content || '');
      editor._loadedForId = form.id;
    }
  }, [form.id, editor]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file after a failed attempt
    if (!file) return;

    // IMAGE-UPLOAD-SIZE-FIX: check client-side, before ever hitting the
    // network — matches backend's own MAX_IMAGE_SIZE (uploads.py). Without
    // this, an oversized file only fails after a full upload attempt, and
    // a misconfigured proxy in front of the API can reject it with its
    // own generic error page before our backend's real validation message
    // is ever seen (confirmed live on a real deployment).
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error(`Image too large — max ${MAX_IMAGE_SIZE_MB}MB (this file is ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      return;
    }

    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authFetch('/api/upload/image', { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setForm((f) => ({ ...f, cover_image_url: data.url }));
    } catch (err) {
      toast.error(err.message || 'Cover image upload failed');
    }
    setUploadingCover(false);
  };

  return (
    <div className="abm-editor-card">
      <div className="abm-field">
        <label className="abm-label">TITLE</label>
        <input
          className="abm-input"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="Post title"
        />
      </div>

      <div className="abm-field">
        <label className="abm-label">URL SLUG (optional — leave blank to auto-generate from the title)</label>
        <input
          className="abm-input"
          value={form.slug || ''}
          onChange={(e) => setForm((f) => ({ ...f, slug: previewSlugify(e.target.value) }))}
          placeholder={previewSlugify(form.title) || 'post-url-slug'}
        />
        <p className="abm-hint">/blog/{form.slug || previewSlugify(form.title) || 'post-url-slug'}</p>
      </div>

      <div className="abm-field">
        <label className="abm-label">EXCERPT (shown in listing cards)</label>
        <textarea
          className="abm-textarea"
          rows={2}
          value={form.excerpt || ''}
          onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
          placeholder="Short summary"
        />
      </div>

      <div className="abm-field">
        <label className="abm-label">FEATURED IMAGE</label>
        <div className="abm-cover-row">
          {form.cover_image_url && <img src={form.cover_image_url} alt="" className="abm-cover-preview" />}
          <label className="abm-upload-btn">
            {uploadingCover ? 'Uploading…' : 'Choose Image'}
            <input type="file" accept="image/*" onChange={handleCoverUpload} hidden disabled={uploadingCover} />
          </label>
        </div>
        <p className="abm-hint">JPEG, PNG, WebP, or GIF · max {MAX_IMAGE_SIZE_MB}MB</p>
      </div>

      <div className="abm-field">
        <label className="abm-label">IMAGE ALT TEXT (for accessibility + SEO)</label>
        <input
          className="abm-input"
          value={form.cover_image_alt || ''}
          onChange={(e) => setForm((f) => ({ ...f, cover_image_alt: e.target.value }))}
          placeholder="Describe the image in one short sentence…"
        />
      </div>

      <div className="abm-field">
        <div className="abm-content-label-row">
          <label className="abm-label">CONTENT</label>
          <button
            type="button"
            className="abm-mode-toggle"
            onClick={() => {
              if (!htmlSourceMode) {
                // Leaving rich text -> HTML source: form.content is already
                // kept current via onUpdate, nothing to sync.
                setHtmlSourceMode(true);
              } else {
                // Leaving HTML source -> rich text: the textarea edited
                // form.content directly while the editor instance sat idle,
                // so it needs to be told about the new content explicitly.
                editor?.commands.setContent(form.content || '');
                setHtmlSourceMode(false);
              }
            }}
          >
            {htmlSourceMode ? '✎ Rich text' : '</> HTML source'}
          </button>
        </div>
        {htmlSourceMode ? (
          <textarea
            className="abm-textarea abm-html-source"
            rows={18}
            value={form.content || ''}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            placeholder="<p>Paste or write raw HTML here…</p>"
            spellCheck={false}
          />
        ) : (
          <div className="abm-richtext-wrap">
            <RichTextToolbar editor={editor} />
            <EditorContent editor={editor} className="abm-richtext-body" />
          </div>
        )}
      </div>

      <SeoSection form={form} setForm={setForm} />

      <div className="abm-field abm-field--row">
        <div>
          <label className="abm-label">STATUS</label>
          <select
            className="abm-select"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div className="abm-editor-actions">
          <button className="abm-btn abm-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="abm-btn abm-btn--primary" onClick={onSave} disabled={saving || !form.title.trim()}>
            {saving ? 'Saving…' : 'Save Post'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlogManagement() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingForm, setEditingForm] = useState(null); // null = list view
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/blog');
      if (!res.ok) throw new Error();
      setPosts(await res.json());
    } catch { toast.error('Failed to load blog posts'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const startCreate = () => setEditingForm({ ...EMPTY_FORM });
  const startEdit = (post) => setEditingForm({
    id: post.id, title: post.title, slug: post.slug || '', excerpt: post.excerpt || '',
    content: post.content, cover_image_url: post.cover_image_url || '', cover_image_alt: post.cover_image_alt || '',
    focus_keyword: post.focus_keyword || '', tags: post.tags || '', seo_title: post.seo_title || '', meta_description: post.meta_description || '',
    status: post.status,
  });
  const cancelEdit = () => setEditingForm(null);

  const handleSave = async () => {
    if (!editingForm.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const isNew = !editingForm.id;
      const url = isNew ? '/api/admin/blog' : `/api/admin/blog/${editingForm.id}`;
      const res = await authFetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingForm.title,
          slug: editingForm.slug,
          excerpt: editingForm.excerpt,
          content: editingForm.content,
          cover_image_url: editingForm.cover_image_url,
          cover_image_alt: editingForm.cover_image_alt,
          focus_keyword: editingForm.focus_keyword,
          tags: editingForm.tags,
          seo_title: editingForm.seo_title,
          meta_description: editingForm.meta_description,
          status: editingForm.status,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success(isNew ? 'Post created' : 'Post updated');
      setEditingForm(null);
      fetchPosts();
    } catch (e) {
      toast.error(e.message || 'Failed to save post');
    }
    setSaving(false);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/admin/blog/${postId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete post'); }
  };

  return (
    <div className="abm-page">
      <style>{CSS}</style>

      <div className="abm-header">
        <div>
          <h2 className="abm-title">Blog</h2>
          <p className="abm-desc">Create and manage blog posts shown on the public website</p>
        </div>
        {!editingForm && <button className="abm-btn abm-btn--primary" onClick={startCreate}>+ New Post</button>}
      </div>

      {editingForm ? (
        <PostEditor form={editingForm} setForm={setEditingForm} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
      ) : loading ? (
        <div className="abm-loading">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="abm-empty">No blog posts yet. Click "+ New Post" to create one.</div>
      ) : (
        <div className="abm-table-wrap">
          <table className="abm-table">
            <thead>
              <tr>
                <th>TITLE</th>
                <th>STATUS</th>
                <th>UPDATED</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id}>
                  <td className="abm-post-title">{post.title}</td>
                  <td>
                    <span className={`abm-status-badge abm-status-badge--${post.status}`}>{post.status}</span>
                  </td>
                  <td className="abm-date">{post.updated_at ? post.updated_at.split('T')[0] : '—'}</td>
                  <td className="abm-row-actions">
                    <button className="abm-link-btn" onClick={() => startEdit(post)}>Edit</button>
                    <button className="abm-link-btn abm-link-btn--danger" onClick={() => handleDelete(post.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CSS = `
.abm-page { padding: 32px 0; max-width: 1100px; margin: 0 auto; }
.abm-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; }
.abm-title { font-size: 20px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.abm-desc { font-size: 13px; color: var(--text-muted); margin: 0; }
.abm-loading, .abm-empty { text-align: center; color: var(--text-muted); padding: 60px 0; font-size: 14px; }

.abm-btn {
  padding: 10px 18px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; white-space: nowrap; border: none;
}
.abm-btn--primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; }
.abm-btn--primary:hover { box-shadow: 0 4px 12px rgba(99,102,241,0.3); }
.abm-btn--primary:disabled { opacity: 0.5; cursor: not-allowed; }
.abm-btn--ghost { background: transparent; border: 1px solid rgba(255,255,255,0.15); color: var(--text-primary); }
[data-theme="light"] .abm-btn--ghost { border-color: rgba(99,102,241,0.2); }

/* Table */
.abm-table-wrap {
  position: relative;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
}
.abm-table-wrap::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0;
}
[data-theme="light"] .abm-table-wrap { background: #ffffff; border-color: rgba(99,102,241,0.11); box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05); }
.abm-table { width: 100%; border-collapse: collapse; }
.abm-table th { text-align: left; padding: 14px 20px; font-size: 10px; font-weight: 700; color: var(--text-muted); letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06); background: rgba(255,255,255,0.02); }
[data-theme="light"] .abm-table th { border-bottom-color: rgba(99,102,241,0.08); background: rgba(248,250,252,0.8); }
.abm-table td { padding: 16px 20px; font-size: 13px; color: var(--text-primary); border-bottom: 1px solid rgba(255,255,255,0.04); }
[data-theme="light"] .abm-table td { border-bottom-color: rgba(99,102,241,0.05); }
.abm-table tr:last-child td { border-bottom: none; }
.abm-post-title { font-weight: 500; }
.abm-date { color: var(--text-muted); }
.abm-row-actions { display: flex; gap: 14px; }
.abm-link-btn { background: none; border: none; color: #a5b4fc; font-size: 12px; font-weight: 600; cursor: pointer; padding: 0; }
[data-theme="light"] .abm-link-btn { color: #6366f1; }
.abm-link-btn--danger { color: #f87171; }
[data-theme="light"] .abm-link-btn--danger { color: #dc2626; }

.abm-status-badge { padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: capitalize; }
.abm-status-badge--draft { background: rgba(245,158,11,0.12); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25); }
.abm-status-badge--published { background: rgba(34,197,94,0.12); color: #4ade80; border: 1px solid rgba(34,197,94,0.25); }

/* Editor */
.abm-editor-card {
  position: relative;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
}
.abm-editor-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0;
}
[data-theme="light"] .abm-editor-card { background: #ffffff; border-color: rgba(99,102,241,0.11); box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05); }
.abm-field { margin-bottom: 18px; }
.abm-field--row { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 0; }
.abm-label { display: block; font-size: 10px; font-weight: 700; color: var(--text-muted); letter-spacing: 0.04em; margin-bottom: 6px; }
.abm-content-label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.abm-content-label-row .abm-label { margin-bottom: 0; }
.abm-mode-toggle {
  font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 6px;
  background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.25);
  color: #a5b4fc; cursor: pointer; font-family: "JetBrains Mono", monospace;
}
.abm-mode-toggle:hover { background: rgba(99,102,241,0.18); }
[data-theme="light"] .abm-mode-toggle { color: #4f46e5; background: rgba(99,102,241,0.08); }
.abm-html-source {
  width: 100%; font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12.5px; line-height: 1.6; min-height: 340px; white-space: pre;
}
.abm-input, .abm-textarea, .abm-select {
  padding: 10px 14px; border-radius: 8px; font-size: 13px; width: 100%;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
  color: var(--text-primary); outline: none; font-family: inherit;
}
[data-theme="light"] .abm-input, [data-theme="light"] .abm-textarea, [data-theme="light"] .abm-select { background: #f8fafc; border-color: rgba(99,102,241,0.15); }
.abm-input:focus, .abm-textarea:focus, .abm-select:focus { border-color: rgba(99,102,241,0.5); }
.abm-select { cursor: pointer; width: auto; min-width: 140px; }
.abm-textarea { resize: vertical; }

.abm-cover-row { display: flex; align-items: center; gap: 14px; }
.abm-cover-preview { width: 96px; height: 64px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); }
.abm-upload-btn {
  padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: var(--text-primary);
}
[data-theme="light"] .abm-upload-btn { background: #f8fafc; border-color: rgba(99,102,241,0.15); }

.abm-richtext-wrap { border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; overflow: hidden; }
[data-theme="light"] .abm-richtext-wrap { border-color: rgba(99,102,241,0.15); }
.abm-toolbar { display: flex; gap: 4px; padding: 8px; border-bottom: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.02); flex-wrap: wrap; }
[data-theme="light"] .abm-toolbar { background: rgba(248,250,252,0.8); border-bottom-color: rgba(99,102,241,0.08); }
.abm-toolbtn {
  padding: 5px 10px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer;
  background: transparent; border: 1px solid transparent; color: var(--text-primary);
}
.abm-toolbtn:hover { background: rgba(255,255,255,0.06); }
.abm-toolbtn--active { background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.3); color: #a5b4fc; }
.abm-richtext-body { padding: 14px; min-height: 220px; font-size: 14px; color: var(--text-primary); }
.abm-richtext-body .ProseMirror { outline: none; min-height: 200px; }
.abm-richtext-body .ProseMirror p { margin: 0 0 10px; }
.abm-richtext-body .ProseMirror img { max-width: 100%; border-radius: 8px; }
/* TABLE-PASTE-FIX: without this, a table node parses/round-trips fine but
   renders with no visible grid at all — a browser's UA stylesheet gives
   <table>/<td> no border by default. */
.abm-richtext-body .ProseMirror table { border-collapse: collapse; width: 100%; margin: 10px 0; }
.abm-richtext-body .ProseMirror th, .abm-richtext-body .ProseMirror td { border: 1px solid rgba(255,255,255,0.18); padding: 8px 10px; text-align: left; }
.abm-richtext-body .ProseMirror th { background: rgba(255,255,255,0.06); font-weight: 600; }
[data-theme="light"] .abm-richtext-body .ProseMirror th,
[data-theme="light"] .abm-richtext-body .ProseMirror td { border-color: rgba(15,23,42,0.15); }
[data-theme="light"] .abm-richtext-body .ProseMirror th { background: rgba(15,23,42,0.04); }

.abm-editor-actions { display: flex; gap: 10px; }

.abm-hint { font-size: 11px; color: var(--text-muted); margin: 6px 0 0; }
.abm-charcount { font-size: 11px; color: var(--text-muted); margin: 6px 0 0; text-align: right; }

.abm-seo-section { margin-top: 28px; padding-top: 22px; border-top: 1px solid rgba(255,255,255,0.08); }
[data-theme="light"] .abm-seo-section { border-top-color: rgba(99,102,241,0.1); }
.abm-seo-heading { font-size: 13px; font-weight: 700; color: var(--text-primary); margin: 0 0 16px; }

.abm-serp-preview { margin-top: 4px; }
.abm-serp-card {
  padding: 14px 16px; border-radius: 8px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
}
[data-theme="light"] .abm-serp-card { background: #fff; border-color: rgba(99,102,241,0.12); }
.abm-serp-url { font-size: 12px; color: #86efac; margin-bottom: 3px; }
[data-theme="light"] .abm-serp-url { color: #15803d; }
.abm-serp-title { font-size: 16px; color: #a5b4fc; line-height: 1.3; margin-bottom: 3px; }
[data-theme="light"] .abm-serp-title { color: #4338ca; }
.abm-serp-desc { font-size: 13px; color: var(--text-muted); line-height: 1.4; }
`;
